import React, { useState } from 'react';
import { DB } from '../data/database';
import { ROLES, getYardLocations } from '../data/constants';
import { useLanguage } from '../translations/LanguageContext';
import { PlusIcon, CheckIcon, EditIcon, DeleteIcon } from './Icons';
import { logActivity } from '../data/notifications';

const ALL_ROLES = [
  { value: 'head_admin',  label: 'Head Admin' },
  { value: 'admin',       label: 'Administrator' },
  { value: 'accountant',  label: 'Accountant' },
  { value: 'uploader',    label: 'Data Entry' },
  { value: 'japan_yard',  label: 'Japan Yard' },
  { value: 'yard_sales',  label: 'Yard Sales' },
  { value: 'destination',    label: 'Destination Team' },
  { value: 'yard_operator',  label: 'Yard Operator' },
  { value: 'yard_manager',   label: 'Yard Manager' },
];


function AddStaffModal({ onClose, onSave, currentUser }) {
  const { t } = useLanguage();
  const isHeadAdmin = currentUser.role === 'head_admin' || currentUser.isHeadAdmin || currentUser.is_head_admin;
  const assignableRoles = isHeadAdmin
    ? ALL_ROLES.filter(r => r.value !== 'head_admin')
    : ALL_ROLES.filter(r => r.value !== 'head_admin' && r.value !== 'admin');

  const [form, setForm] = useState({ name: '', email: '', password: '', role: 'uploader', phone: '', yard: '' });
  const [error, setError] = useState('');
  const update = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const handleSubmit = (e) => {
    e.preventDefault();
    setError('');
    if (!form.name.trim() || !form.email.trim() || !form.password.trim()) {
      setError('Name, username and password are required.');
      return;
    }
    const users = DB.get('users') || [];
    if (users.find(u => u.email === form.email.trim())) {
      setError('A user with that username already exists.');
      return;
    }
    // Regular admins cannot create admin accounts
    if (!isHeadAdmin && form.role === 'admin') {
      setError('Only Head Admin can create Administrator accounts.');
      return;
    }
    const newUser = {
      id: DB.genId('U'),
      name: form.name.trim(),
      email: form.email.trim(),
      password: form.password.trim(),
      phone: form.phone.trim(),
      role: form.role,
      yard: form.yard || null,
      createdAt: new Date().toISOString(),
    };
    DB.set('users', [...users, newUser]);
    logActivity.staffAdded?.(form.name.trim(), ALL_ROLES.find(r => r.value === form.role)?.label || form.role, currentUser.name);
    onSave();
    onClose();
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 480 }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3 className="modal-title">Add Staff Member</h3>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            {error && <div className="login-error" style={{ marginBottom: 14 }}>{error}</div>}
            <div className="form-group">
              <label className="form-label">Full Name <span className="req">*</span></label>
              <input className="form-input" value={form.name} onChange={e => update('name', e.target.value)} required />
            </div>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Username / Login <span className="req">*</span></label>
                <input className="form-input" value={form.email} onChange={e => update('email', e.target.value)} required />
              </div>
              <div className="form-group">
                <label className="form-label">Phone</label>
                <input className="form-input" value={form.phone} onChange={e => update('phone', e.target.value)} />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Password <span className="req">*</span></label>
                <input className="form-input" type="password" value={form.password} onChange={e => update('password', e.target.value)} required />
              </div>
              <div className="form-group">
                <label className="form-label">Role</label>
                <select className="form-select" value={form.role} onChange={e => update('role', e.target.value)}>
                  {assignableRoles.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                </select>
              </div>
            </div>
            {(form.role === 'yard_operator' || form.role === 'yard_manager') && (
            <div className="form-group">
              <label className="form-label">Assigned Yard</label>
              <select className="form-select" value={form.yard} onChange={e => update('yard', e.target.value)}>
                <option value="">— None —</option>
                {getYardLocations().map(y => <option key={y.id} value={y.id}>{y.flag} {y.name}</option>)}
              </select>
            </div>
            )}
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>{t('cancel')}</button>
            <button type="submit" className="btn btn-primary"><CheckIcon /> Add Staff Member</button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Reset password modal
function ResetPasswordModal({ target, onClose }) {
  const tempPassword = Math.random().toString(36).slice(2, 6) + Math.random().toString(36).slice(2, 6).toUpperCase();
  const [saved, setSaved] = useState(false);

  const handleReset = () => {
    const users = DB.get('users') || [];
    DB.set('users', users.map(u => u.id === target.id ? { ...u, password: tempPassword } : u));
    setSaved(true);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 420 }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3 className="modal-title">Reset Password — {target.name}</h3>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">
          {!saved ? (
            <>
              <p style={{ fontSize: 13, color: 'var(--text2)', marginBottom: 16 }}>
                This will generate a new temporary password for <strong>{target.name}</strong>. They must change it on next login.
              </p>
              <button className="btn btn-primary" style={{ width: '100%' }} onClick={handleReset}>Generate New Password</button>
            </>
          ) : (
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 8 }}>Temporary password (show only once):</div>
              <div style={{
                padding: '14px 20px', background: 'var(--bg3)', borderRadius: 8, fontFamily: 'monospace',
                fontSize: 20, fontWeight: 700, letterSpacing: 2, color: 'var(--green)', marginBottom: 12,
                userSelect: 'all', cursor: 'copy',
              }}>
                {tempPassword}
              </div>
              <button className="btn btn-secondary btn-sm" onClick={() => {
                navigator.clipboard?.writeText(tempPassword);
              }}>Copy to Clipboard</button>
              <div style={{ fontSize: 11, color: 'var(--yellow)', marginTop: 12 }}>
                This password will not be shown again. Make sure the employee saves it.
              </div>
            </div>
          )}
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}

function Staff({ user }) {
  const { t } = useLanguage();
  const [, forceUpdate] = useState(0);
  const [showAdd, setShowAdd] = useState(false);
  const [resetTarget, setResetTarget] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editRole, setEditRole] = useState('');
  const [viewProfile, setViewProfile] = useState(null);
  const [editProfile, setEditProfile] = useState(null);

  const users = DB.get('users') || [];
  const refresh = () => forceUpdate(v => v + 1);

  const isHeadAdmin = user.role === 'head_admin' || user.isHeadAdmin || user.is_head_admin;
  const isAdmin = user.role === 'admin' || isHeadAdmin;

  // Permission: can this user manage the target?
  const canManage = (target) => {
    if (target.id === user.id) return false; // can't manage yourself
    if (isHeadAdmin) return true; // head admin manages everyone
    if (!isAdmin) return false;
    // Regular admin cannot manage other admins or head_admin
    if (target.role === 'head_admin' || target.isHeadAdmin) return false;
    if (target.role === 'admin') return false;
    return true;
  };

  // Filter: regular admins don't see head_admin profile
  const visibleUsers = isHeadAdmin ? users : users.filter(u => u.role !== 'head_admin' && !u.isHeadAdmin);

  const handleFreeze = (target) => {
    if (!canManage(target)) return;
    const msg = target.frozen
      ? `Unfreeze ${target.name}'s account?`
      : `Freeze ${target.name}'s account? They will be locked out immediately.`;
    if (!window.confirm(msg)) return;
    DB.set('users', (DB.get('users') || []).map(u => u.id === target.id ? { ...u, frozen: !target.frozen } : u));
    refresh();
  };

  const handleDelete = (target) => {
    if (!canManage(target)) return;
    if (!window.confirm(`Permanently delete ${target.name}'s account?`)) return;
    DB.set('users', (DB.get('users') || []).filter(u => u.id !== target.id));
    refresh();
  };

  const handleRoleChange = (targetId) => {
    if (!editRole) return;
    // Regular admins cannot promote to admin
    if (!isHeadAdmin && editRole === 'admin') return;
    DB.set('users', (DB.get('users') || []).map(u => u.id === targetId ? { ...u, role: editRole } : u));
    setEditingId(null);
    setEditRole('');
    refresh();
  };

  const assignableRoles = isHeadAdmin
    ? ALL_ROLES.filter(r => r.value !== 'head_admin')
    : ALL_ROLES.filter(r => r.value !== 'head_admin' && r.value !== 'admin');

  // ── Profile View ──
  if (viewProfile) {
    const p = (DB.get('users') || []).find(u => u.id === viewProfile);
    if (!p) { setViewProfile(null); return null; }
    const roleName = ROLES[p.role]?.name || p.role;
    const activity = (() => {
      try {
        const { Notifications } = require('../data/notifications');
        return Notifications.getAll().filter(n => n.data?.addedBy === p.name || n.data?.assignedBy === p.name || n.data?.loadedBy === p.name || n.data?.verifiedBy === p.name || n.data?.sentBy === p.name).slice(0, 20);
      } catch { return []; }
    })();

    return (
      <div>
        <button className="btn btn-secondary btn-sm" onClick={() => setViewProfile(null)} style={{ marginBottom: 16 }}>← Back to Staff List</button>

        {/* Profile Header */}
        <div className="card" style={{ marginBottom: 16 }}>
          <div className="card-body" style={{ display: 'flex', gap: 20, alignItems: 'center', flexWrap: 'wrap' }}>
            <div style={{
              width: 64, height: 64, borderRadius: 12, background: 'var(--bg3)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 24, fontWeight: 700, color: 'var(--text3)', overflow: 'hidden', flexShrink: 0,
            }}>
              {p.avatar ? <img src={p.avatar} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : p.name?.charAt(0).toUpperCase()}
            </div>
            <div style={{ flex: 1, minWidth: 200 }}>
              <h2 style={{ margin: 0 }}>{p.name}</h2>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 4, flexWrap: 'wrap' }}>
                <span className={`status ${p.role === 'head_admin' ? 'status-transit' : 'status-available'}`} style={{ fontSize: 10 }}>{roleName}</span>
                {p.frozen
                  ? <span className="status" style={{ background: 'rgba(248,81,73,0.12)', color: 'var(--red)', fontSize: 10 }}>Frozen</span>
                  : <span className="status" style={{ background: 'rgba(63,185,80,0.12)', color: 'var(--green)', fontSize: 10 }}>Active</span>}
              </div>
              <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 6 }}>
                {p.email && <span style={{ fontFamily: 'monospace', color: 'var(--cyan)' }}>{p.email}</span>}
                {p.phone && <span style={{ marginLeft: 12 }}>{p.phone}</span>}
              </div>
              {p.createdAt && <div style={{ fontSize: 10, color: 'var(--text3)', marginTop: 2 }}>Joined: {p.createdAt?.split('T')[0] || '—'}</div>}
            </div>
            {canManage(p) && (
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', flexShrink: 0 }}>
                <button className="btn btn-secondary btn-sm" onClick={() => {
                  setEditProfile({ ...p });
                }}>
                  <EditIcon /> Edit Profile
                </button>
                {isHeadAdmin && <button className="btn btn-secondary btn-sm" onClick={() => setResetTarget(p)}>Reset Password</button>}
                <button className="btn btn-sm" style={{
                  background: p.frozen ? 'rgba(63,185,80,0.15)' : 'rgba(230,162,60,0.12)',
                  color: p.frozen ? 'var(--green)' : 'var(--yellow)',
                  border: `1px solid ${p.frozen ? 'rgba(63,185,80,0.35)' : 'rgba(230,162,60,0.35)'}`,
                }} onClick={() => handleFreeze(p)}>
                  {p.frozen ? 'Unfreeze' : 'Freeze'}
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Recent Activity */}
        <div className="card">
          <div className="card-header">
            <div className="card-title">Recent Activity</div>
            <span style={{ fontSize: 11, color: 'var(--text3)' }}>{activity.length} actions</span>
          </div>
          <div className="card-body" style={{ padding: activity.length > 0 ? 0 : '20px', maxHeight: 400, overflowY: 'auto' }}>
            {activity.length === 0 ? (
              <div style={{ textAlign: 'center', color: 'var(--text3)', fontSize: 13 }}>No recorded activity</div>
            ) : activity.map((n, i) => (
              <div key={n.id || i} style={{ padding: '10px 16px', borderBottom: i < activity.length - 1 ? '1px solid var(--border)' : 'none', fontSize: 12 }}>
                <div style={{ color: 'var(--text2)' }}>{n.message}</div>
                <div style={{ fontSize: 10, color: 'var(--text3)', marginTop: 2 }}>{n.title} · {(() => { try { return new Date(n.createdAt).toLocaleString(); } catch { return ''; } })()}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Edit Profile Modal */}
        {editProfile && (
          <div className="modal-overlay" onClick={() => setEditProfile(null)}>
            <div className="modal" style={{ maxWidth: 480 }} onClick={e => e.stopPropagation()}>
              <div className="modal-header">
                <h3 className="modal-title">Edit — {editProfile.name}</h3>
                <button className="modal-close" onClick={() => setEditProfile(null)}>×</button>
              </div>
              <div className="modal-body">
                <div className="form-group">
                  <label className="form-label">Name</label>
                  <input className="form-input" value={editProfile.name || ''} onChange={e => setEditProfile(prev => ({ ...prev, name: e.target.value }))} />
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Email / Username</label>
                    <input className="form-input" value={editProfile.email || ''} onChange={e => setEditProfile(prev => ({ ...prev, email: e.target.value }))} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Phone</label>
                    <input className="form-input" value={editProfile.phone || ''} onChange={e => setEditProfile(prev => ({ ...prev, phone: e.target.value }))} />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Role</label>
                    <select className="form-select" value={editProfile.role} onChange={e => setEditProfile(prev => ({ ...prev, role: e.target.value }))}>
                      {assignableRoles.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                    </select>
                  </div>
                  {isHeadAdmin && (
                    <div className="form-group">
                      <label className="form-label">Password</label>
                      <input className="form-input" value={editProfile.password || ''} onChange={e => setEditProfile(prev => ({ ...prev, password: e.target.value }))} placeholder="Enter new password" />
                    </div>
                  )}
                </div>
                {(editProfile.role === 'yard_operator' || editProfile.role === 'yard_manager') && (
                <div className="form-group">
                  <label className="form-label">Assigned Yard</label>
                  <select className="form-select" value={editProfile.yard || ''} onChange={e => setEditProfile(prev => ({ ...prev, yard: e.target.value }))}>
                    <option value="">— None —</option>
                    {getYardLocations().map(y => <option key={y.id} value={y.id}>{y.flag} {y.name}</option>)}
                  </select>
                </div>
                )}
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={() => setEditProfile(null)}>{t('cancel')}</button>
                <button className="btn btn-primary" onClick={() => {
                  if (!isHeadAdmin && editProfile.role === 'admin') { alert('Only Head Admin can assign Administrator role.'); return; }
                  const updates = { name: editProfile.name?.trim(), email: editProfile.email?.trim(), phone: editProfile.phone?.trim(), role: editProfile.role, yard: editProfile.yard || null };
                  if (isHeadAdmin && editProfile.password) updates.password = editProfile.password;
                  DB.set('users', (DB.get('users') || []).map(u => u.id === editProfile.id ? { ...u, ...updates } : u));
                  setEditProfile(null); setViewProfile(editProfile.id); refresh();
                }}><CheckIcon /> Save Changes</button>
              </div>
            </div>
          </div>
        )}

        {resetTarget && <ResetPasswordModal target={resetTarget} onClose={() => { setResetTarget(null); refresh(); setViewProfile(viewProfile); }} />}
      </div>
    );
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <h2>{t('employees')}</h2>
        {isAdmin && (
          <button className="btn btn-primary" onClick={() => setShowAdd(true)}>
            <PlusIcon /> Add Staff
          </button>
        )}
      </div>

      <div className="card">
        <div className="card-body" style={{ padding: 0 }}>
          <table className="data-table">
            <thead>
              <tr>
                <th>{t('name')}</th>
                <th>Username</th>
                {isHeadAdmin && <th>Password</th>}
                <th>Role</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {visibleUsers.map(u => {
                const isSelf = u.id === user.id;
                const roleName = ROLES[u.role]?.name || u.role;
                const manageable = canManage(u);
                return (
                  <tr key={u.id} style={u.frozen ? { opacity: 0.55 } : {}}>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <div style={{
                          width: 32, height: 32, borderRadius: 8, background: 'var(--bg3)',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          fontSize: 14, fontWeight: 700, color: 'var(--text3)', flexShrink: 0,
                          overflow: 'hidden',
                        }}>
                          {u.avatar ? <img src={u.avatar} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : u.name?.charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <strong>{u.name}</strong>
                          {isSelf && <span style={{ marginLeft: 6, fontSize: 10, color: 'var(--text3)' }}>(you)</span>}
                          {u.phone && <div style={{ fontSize: 10, color: 'var(--text3)' }}>{u.phone}</div>}
                        </div>
                      </div>
                    </td>
                    <td style={{ fontFamily: 'monospace', color: 'var(--cyan)', fontSize: 12 }}>{u.email}</td>
                    {isHeadAdmin && (
                      <td style={{ fontFamily: 'monospace', fontSize: 11, color: 'var(--text3)' }}>
                        {u.password || '—'}
                      </td>
                    )}
                    <td>
                      {editingId === u.id ? (
                        <div style={{ display: 'flex', gap: 4 }}>
                          <select className="form-select" value={editRole} onChange={e => setEditRole(e.target.value)} style={{ fontSize: 11, padding: '2px 4px' }}>
                            {assignableRoles.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                          </select>
                          <button className="btn btn-success btn-sm" style={{ padding: '2px 6px' }} onClick={() => handleRoleChange(u.id)}><CheckIcon /></button>
                          <button className="btn btn-secondary btn-sm" style={{ padding: '2px 6px' }} onClick={() => setEditingId(null)}>×</button>
                        </div>
                      ) : (
                        <span className={`status ${u.role === 'head_admin' ? 'status-transit' : 'status-available'}`} style={{ fontSize: 10 }}>
                          {roleName}
                        </span>
                      )}
                    </td>
                    <td>
                      {u.frozen
                        ? <span className="status" style={{ background: 'rgba(248,81,73,0.12)', color: 'var(--red)', border: '1px solid rgba(248,81,73,0.25)', fontSize: 10 }}>Frozen</span>
                        : <span className="status" style={{ background: 'rgba(63,185,80,0.12)', color: 'var(--green)', border: '1px solid rgba(63,185,80,0.25)', fontSize: 10 }}>Active</span>
                      }
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                        <button className="btn btn-secondary btn-sm" style={{ fontSize: 10, padding: '3px 6px' }}
                          onClick={() => setViewProfile(u.id)}>
                          View
                        </button>
                        {manageable && (
                          <>
                            {isHeadAdmin && (
                              <button className="btn btn-secondary btn-sm" style={{ fontSize: 10, padding: '3px 6px' }}
                                onClick={() => setResetTarget(u)}>
                                Reset PW
                              </button>
                            )}
                            <button className="btn btn-secondary btn-sm" style={{ fontSize: 10, padding: '3px 6px' }}
                              onClick={() => { setEditingId(u.id); setEditRole(u.role); }}>
                              <EditIcon /> Role
                            </button>
                            <button className="btn btn-sm" style={{ fontSize: 10, padding: '3px 6px',
                              background: u.frozen ? 'rgba(63,185,80,0.15)' : 'rgba(230,162,60,0.12)',
                              color: u.frozen ? 'var(--green)' : 'var(--yellow)',
                              border: `1px solid ${u.frozen ? 'rgba(63,185,80,0.35)' : 'rgba(230,162,60,0.35)'}`,
                            }} onClick={() => handleFreeze(u)}>
                              {u.frozen ? 'Unfreeze' : 'Freeze'}
                            </button>
                            <button className="btn btn-sm" style={{ fontSize: 10, padding: '3px 6px',
                              background: 'rgba(248,81,73,0.1)', color: 'var(--red)', border: '1px solid rgba(248,81,73,0.3)',
                            }} onClick={() => handleDelete(u)}>
                              <DeleteIcon />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Head Admin only: System Management */}
      {isHeadAdmin && (
        <div className="card" style={{ marginTop: 20 }}>
          <div className="card-header">
            <div className="card-title">System Management</div>
            <span style={{ fontSize: 10, color: 'var(--cyan)' }}>Head Admin Only</span>
          </div>
          <div className="card-body">
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              <button className="btn btn-secondary" onClick={() => {
                const data = DB.export();
                const blob = new Blob([data], { type: 'application/json' });
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a'); a.href = url; a.download = `bigstar_backup_${new Date().toISOString().slice(0,10)}.json`; a.click(); URL.revokeObjectURL(url);
              }}>
                Export Database Backup
              </button>
              <button className="btn btn-secondary" style={{ color: 'var(--red)', borderColor: 'var(--red)' }}
                onClick={() => {
                  const input = prompt('Type DELETE ALL DATA to confirm:');
                  if (input !== 'DELETE ALL DATA') return;
                  if (!window.confirm('FINAL WARNING: This will erase ALL data. Are you absolutely sure?')) return;
                  DB.reset().then(() => window.location.reload());
                }}>
                Clear All Data
              </button>
            </div>
          </div>
        </div>
      )}

      {showAdd && <AddStaffModal onClose={() => setShowAdd(false)} onSave={refresh} currentUser={user} />}
      {resetTarget && <ResetPasswordModal target={resetTarget} onClose={() => { setResetTarget(null); refresh(); }} />}
    </div>
  );
}

export default Staff;
